import { DeliveryTypes } from 'core/constants/delivery-types';
import { SharpsBinOptions } from 'core/constants/sharps-bin-options';
import { StorageRequirements } from 'core/constants/storage-requirement';
import { WasteCollectionOptions } from 'core/constants/waste-collection';
import { ControlType } from 'core/enums/control-type';
import { InputType } from 'core/enums/input-type';
import { getToday, getTomorrow } from 'core/utilities/date-helpers';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { DeliveryRouteAssignments } from 'core/constants/delivery-route-assignment';
import { TimeWindows } from 'core/constants/time-window';
import { ClinicCodes } from 'core/constants/clinic-code';
import { FuelTypes } from 'core/constants/fuel-types';
import { Frequencies } from 'core/constants/frequency';
import { VehicleCheckDefaultOptions } from 'core/constants/vehicle-check-default-options';

const buildTimeOptions = (startHour: number, endHour: number, stepMinutes: number): string[] => {
  const times: string[] = [];
  for (let minutes = startHour * 60; minutes <= endHour * 60; minutes += stepMinutes) {
    const hh = Math.floor(minutes / 60).toString().padStart(2, '0');
    const mm = (minutes % 60).toString().padStart(2, '0');
    times.push(`${hh}:${mm}`);
  }
  return times;
};

const RUN_LOCATION_TIME_OPTIONS = buildTimeOptions(6, 22, 5);

export const LoginFormFields: ISharedFormField[] = [
  {
    key: 'emailAddress',
    control: ControlType.TextField,
    type: InputType.Email,
    label: 'Email address',
    required: true,
  },
  {
    key: 'password',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'Password',
    required: true,
  },
];

export const ForgotPasswordFormFields: ISharedFormField[] = [
  {
    key: 'emailAddress',
    control: ControlType.TextField,
    type: InputType.Email,
    label: 'Email address',
    required: true,
  },
];

export const ResetPasswordFormFields: ISharedFormField[] = [
  {
    key: 'newPassword',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'New Password',
    required: true,
  },
  {
    key: 'confirmNewPassword',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'Confirm Password',
    required: true,
  },
];

export const SetPasswordFormFields: ISharedFormField[] = [
  {
    key: 'currentPassword',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'Current Password',
    required: true,
  },
  {
    key: 'newPassword',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'New Password',
    required: true,
  },
  {
    key: 'confirmNewPassword',
    control: ControlType.TextField,
    type: InputType.Password,
    label: 'Confirm New Password',
    required: true,
  },
];

export const AddDeliveryFormFields: ISharedFormField[] = [
  {
    key: 'route',
    control: ControlType.Select,
    options: DeliveryRouteAssignments,
    label: 'Route',
    required: true,
    defaultValue: DeliveryRouteAssignments[0],
  },
  {
    key: 'firstName',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'First name',
    required: true,
  },
  {
    key: 'lastName',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Last name',
    required: true,
  },
  {
    key: 'hospitalNumber',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Hospital number',
    required: false,
  },
  {
    key: 'date',
    control: ControlType.DatePicker,
    label: 'Delivery date',
    required: true,
    defaultValue: getTomorrow(),
    minDate: getToday(),
  },
  {
    key: 'timeWindow',
    control: ControlType.Select,
    options: TimeWindows,
    label: 'Time window',
    required: true,
    defaultValue: TimeWindows[0],
  },
  {
    key: 'address',
    control: ControlType.AddressSearch,
    label: 'Address',
    required: true,
  },
  {
    key: 'phoneNumber',
    control: ControlType.TextField,
    type: InputType.Tel,
    label: 'Phone number',
    required: true,
  },
  {
    key: 'parcelCount',
    control: ControlType.TextField,
    type: InputType.Number,
    label: 'Number of parcels',
    required: true,
    defaultValue: '1',
  },
  {
    key: 'clinicCode',
    control: ControlType.Select,
    options: ClinicCodes,
    label: 'Clinic code',
    required: true,
  },
  {
    key: 'sharpsBin',
    control: ControlType.Select,
    label: 'Sharps bin required',
    required: true,
    options: SharpsBinOptions.map((sbo) => ({
      label: sbo.label,
      value: sbo.key,
    })),
  },
  {
    key: 'wasteCollection',
    control: ControlType.Select,
    label: 'Waste collection required',
    required: true,
    options: WasteCollectionOptions.map((wc) => ({
      label: wc.label,
      value: wc.key,
    })),
  },
  {
    key: 'storageRequirement',
    control: ControlType.Select,
    label: 'Storage requirement',
    required: true,
    options: StorageRequirements.map((sr) => ({
      label: sr.label,
      value: sr.key,
    })),
  },
  {
    key: 'type',
    control: ControlType.Select,
    label: 'Delivery type',
    required: true,
    options: DeliveryTypes.map((type) => ({ value: type.key, label: type.label })),
  },
  {
    key: 'notes',
    control: ControlType.TextArea,
    label: 'Additional notes',
    required: false,
  },
];

export const SuccessfulDeliveryFormFields = [
  {
    key: 'note',
    control: ControlType.TextArea,
    label: 'Optional note',
    required: false,
  },
];

export const ExportDeliveriesDialogFormFields = [
  {
    key: 'exportType',
    control: ControlType.Select,
    label: 'Export type',
    options: [
      {
        label: 'CSV',
        value: 'csv',
      },
      {
        label: 'Successful Deliveries PDF',
        value: 'successfulDeliveriesPdf',
      },
    ],
    defaultValue: {
      label: 'CSV',
      value: 'csv',
    },
    required: true,
  },
  {
    key: 'startDateString',
    control: ControlType.DatePicker,
    label: 'Date from',
    required: true,
    defaultValue: getToday(),
  },
  {
    key: 'endDateString',
    control: ControlType.DatePicker,
    label: 'Date to',
    required: true,
    defaultValue: getToday(),
  },
];

export const ChangeDeliveryStatusFormFields: ISharedFormField[] = [
  {
    key: 'status',
    control: ControlType.Select,
    label: 'New status',
    required: true,
    placeholder: 'Select status',
    options: [
      { label: 'Delivered', value: 'delivered' },
      { label: 'Unfulfilled', value: 'unfulfilled' },
    ],
  },
  {
    key: 'deliveredAt',
    control: ControlType.DatePicker,
    label: 'Delivered on',
    helperMessage: 'Required when the status is set to Delivered.',
    required: false,
    defaultValue: getToday(),
  },
  {
    key: 'driverNote',
    control: ControlType.TextArea,
    label: 'Comment',
    helperMessage: 'Required when the status is set to Unfulfilled.',
    required: false,
  },
];

export const RescheduleDeliveryDialogFormFields: ISharedFormField[] = [
  {
    key: 'date',
    control: ControlType.DatePicker,
    label: 'New delivery date',
    required: true,
    defaultValue: getTomorrow(),
    minDate: getTomorrow(),
  },
  {
    key: 'notes',
    control: ControlType.TextArea,
    label: 'Additional notes',
    required: false,
  },
];

export const AddLocationFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Location name',
    required: true,
  },
  {
    key: 'address',
    control: ControlType.AddressSearch,
    label: 'Address',
    required: true,
  },
  {
    key: 'phoneNumber',
    control: ControlType.TextField,
    type: InputType.Tel,
    label: 'Phone number',
    required: false,
  },
  {
    key: 'emailAddress',
    control: ControlType.TextField,
    type: InputType.Email,
    label: 'Email address',
    required: false,
  },
];

export const AddSubLocationFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Sub location name',
    required: true,
  },
  {
    key: 'notes',
    control: ControlType.TextArea,
    label: 'Notes',
    required: false,
  },
];

export const AddEditVehicleFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'registration',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Registration',
    required: true,
  },
  {
    key: 'make',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Make',
    required: true,
  },
  {
    key: 'model',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Model',
    required: true,
  },
  {
    key: 'fuelType',
    control: ControlType.Select,
    label: 'Fuel type',
    required: true,
    options: FuelTypes,
  },
];

export const AcknowledgeAlertFormFields: ISharedFormField[] = [
  {
    key: 'comment',
    control: ControlType.TextArea,
    label: 'Comment',
    required: true,
  },
];

export const AddEditSettingsVehicleCheckFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Name',
    required: true,
  },
  {
    key: 'options',
    control: ControlType.SelectCreate,
    placeholder: 'Create new option...',
    label: 'Options',
    required: true,
    options: VehicleCheckDefaultOptions,
  },
  {
    key: 'requireComment',
    control: ControlType.SelectMulti,
    label: 'Require Comment',
    helperMessage: 'Select the options that require a comment.',
    required: false,
    isDisabled: true,
    options: [],
  },
  {
    key: 'frequency',
    control: ControlType.SelectMulti,
    label: 'Frequency',
    required: true,
    options: Frequencies,
  },
];

export const AddEditProcessTypeFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Name',
    required: true,
  },
  {
    key: 'requireBarcode',
    control: ControlType.Switch,
    label: 'Require Barcode',
    required: true,
    defaultValue: false,
  },
  {
    key: 'requireSignature',
    control: ControlType.Switch,
    label: 'Require Signature',
    required: true,
    defaultValue: false,
  },
  {
    key: 'requirePhoto',
    control: ControlType.Switch,
    label: 'Require Photo',
    required: true,
    defaultValue: false,
  },
  {
    key: 'isPickupDropoff',
    control: ControlType.Switch,
    label: 'Is pickup/dropoff',
    required: true,
    defaultValue: false,
  },
  {
    key: 'instructions',
    control: ControlType.TextArea,
    label: 'Instructions',
    required: false,
  },
];

export const AddEditProcessTypeStatusFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Name',
    required: true,
  },
];

export const AddEditRunFormFields: ISharedFormField[] = [
  {
    key: 'active',
    control: ControlType.Switch,
    label: 'Active',
    required: true,
    defaultValue: true,
  },
  {
    key: 'name',
    control: ControlType.TextField,
    type: InputType.Text,
    label: 'Name',
    required: true,
  },
  {
    key: 'notes',
    control: ControlType.TextArea,
    label: 'Notes',
    required: false,
  },
];

export const AddEditRunLocationsFormFields: ISharedFormField[] = [
  {
    key: 'locationId',
    control: ControlType.Select,
    type: InputType.Text,
    label: 'Location',
    required: true,
    options: [],
  },
  {
    key: 'subLocationId',
    control: ControlType.Select,
    type: InputType.Text,
    label: 'Sub location',
    required: false,
    isDisabled: true,
    options: [],
  },
  {
    key: 'processTypeIds',
    control: ControlType.SelectMulti,
    label: 'Process types',
    required: false,
    options: [],
  },
  {
    key: 'scheduledArrivalTime',
    control: ControlType.TimePicker,
    label: 'Scheduled arrival time',
    required: false,
    placeholder: 'Select time',
    options: [],
    times: RUN_LOCATION_TIME_OPTIONS,
  },
  {
    key: 'scheduledDepartureTime',
    control: ControlType.TimePicker,
    label: 'Scheduled departure time',
    placeholder: 'Select time',
    required: false,
    options: [],
    times: RUN_LOCATION_TIME_OPTIONS,
  },
  {
    key: 'notes',
    control: ControlType.TextArea,
    label: 'Notes',
    required: false,
  },
  {
    key: 'daysOfWeek',
    control: ControlType.DayOfTheWeekPicker,
    label: 'Days of week',
    required: true,
  },
];

export const EditAlertRadiusFormFields: ISharedFormField[] = [
  {
    key: 'alertRadius',
    control: ControlType.TextField,
    type: InputType.Number,
    label: 'Alert radius (metres)',
    required: true,
  },
];
